import { db } from '@/config/firebase'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { doc, getDoc, setDoc } from 'firebase/firestore'

const OUTLIERS_COLLECTION = 'ols_training_config'
const OUTLIERS_DOC_ID = 'weighted_least_squares'

interface OLSOutlierConfigDoc {
  categories?: Record<string, string[]>
  updatedAt?: string
}

const getOutliersDocRef = () => doc(db, OUTLIERS_COLLECTION, OUTLIERS_DOC_ID)

const getOutliersForCategory = (data: OLSOutlierConfigDoc | undefined, categoryKey: string): string[] => {
  if (!data?.categories || !categoryKey) return []
  return data.categories[categoryKey] || []
}

export const useOLSOutliers = (categoryKey: string) => {
  const queryClient = useQueryClient()
  const { showSuccess, showError } = useSnackbar()

  const outliersQuery = useQuery({
    queryKey: ['ols_outliers', categoryKey],
    queryFn: async (): Promise<string[]> => {
      const snapshot = await getDoc(getOutliersDocRef())
      const data = snapshot.exists() ? (snapshot.data() as OLSOutlierConfigDoc) : undefined
      return getOutliersForCategory(data, categoryKey)
    },
    enabled: !!categoryKey,
    staleTime: 1000 * 60,
  })

  const updateOutliers = useMutation({
    mutationFn: async ({ vehicleId, action }: { vehicleId: string; action: 'add' | 'remove' }) => {
      const docRef = getOutliersDocRef()
      const snapshot = await getDoc(docRef)
      const data = snapshot.exists() ? (snapshot.data() as OLSOutlierConfigDoc) : {}
      const categories = data.categories || {}
      const currentIds = categories[categoryKey] || []

      const nextIds = action === 'add'
        ? Array.from(new Set([...currentIds, vehicleId]))
        : currentIds.filter(id => id !== vehicleId)

      await setDoc(docRef, {
        categories: {
          ...categories,
          [categoryKey]: nextIds,
        },
        updatedAt: new Date().toISOString(),
      }, { merge: true })

      return { action, vehicleId }
    },
    onSuccess: ({ action }) => {
      queryClient.invalidateQueries({ queryKey: ['ols_outliers', categoryKey] })
      queryClient.invalidateQueries({ queryKey: ['formula-stat'] })
      showSuccess(action === 'add' ? 'Outlier excluded from training data.' : 'Outlier restored to training data.')
    },
    onError: (error: Error) => {
      showError(error.message)
    },
  })

  return {
    outlierVehicleIds: outliersQuery.data || [],
    isLoading: outliersQuery.isLoading || updateOutliers.isPending,
    addOutlier: (vehicleId: string) => updateOutliers.mutateAsync({ vehicleId, action: 'add' }),
    removeOutlier: (vehicleId: string) => updateOutliers.mutateAsync({ vehicleId, action: 'remove' }),
  }
}

/**
 * Loads all excluded outlier vehicle IDs across every category in one read.
 * Use this when you need to account for outliers globally (e.g. the pricing models table).
 */
export const useAllOLSOutliers = () => {
  const query = useQuery({
    queryKey: ['ols_outliers_all'],
    queryFn: async (): Promise<string[]> => {
      const snapshot = await getDoc(getOutliersDocRef())
      if (!snapshot.exists()) return []
      const data = snapshot.data() as OLSOutlierConfigDoc
      const allIds = Object.values(data.categories || {}).flat()
      return Array.from(new Set(allIds))
    },
    staleTime: 1000 * 60,
  })

  return {
    allOutlierVehicleIds: query.data || [],
    isLoading: query.isLoading,
  }
}
